import { X } from "lucide-react";
import {
  EMPTY_COMPANY_FILTERS,
  type CompanyFilterState,
} from "./company-filters";
import { EMPLOYEE_RANGES } from "@/components/people/people-constants";

export type FilterChip = {
  id: string;
  label: string;
  clear: (prev: CompanyFilterState) => CompanyFilterState;
};

export function buildCompanyFilterChips(
  value: CompanyFilterState,
): FilterChip[] {
  const chips: FilterChip[] = [];

  if (value.personaIcpId) {
    chips.push({
      id: "persona",
      label: `Persona: ${value.personaIcpId}`,
      clear: (p) => ({ ...p, personaIcpId: undefined }),
    });
  }
  if (value.qOrganizationName.trim()) {
    chips.push({
      id: "name",
      label: `Name: ${value.qOrganizationName}`,
      clear: (p) => ({ ...p, qOrganizationName: "" }),
    });
  }
  for (const t of value.qOrganizationKeywordTags) {
    chips.push({
      id: `kw-${t}`,
      label: `Tag: ${t}`,
      clear: (p) => ({
        ...p,
        qOrganizationKeywordTags: p.qOrganizationKeywordTags.filter(
          (x) => x !== t,
        ),
      }),
    });
  }
  for (const loc of value.organizationLocations) {
    chips.push({
      id: `loc-${loc}`,
      label: `HQ: ${loc}`,
      clear: (p) => ({
        ...p,
        organizationLocations: p.organizationLocations.filter((x) => x !== loc),
      }),
    });
  }
  for (const loc of value.organizationNotLocations) {
    chips.push({
      id: `excl-${loc}`,
      label: `Exclude: ${loc}`,
      clear: (p) => ({
        ...p,
        organizationNotLocations: p.organizationNotLocations.filter(
          (x) => x !== loc,
        ),
      }),
    });
  }
  for (const id of value.organizationNumEmployeesRanges) {
    const opt = EMPLOYEE_RANGES.find((o) => o.id === id);
    chips.push({
      id: `emp-${id}`,
      label: `# Emp: ${opt?.label ?? id}`,
      clear: (p) => ({
        ...p,
        organizationNumEmployeesRanges:
          p.organizationNumEmployeesRanges.filter((x) => x !== id),
      }),
    });
  }
  for (const d of value.organizationDomains) {
    chips.push({
      id: `dom-${d}`,
      label: `Domain: ${d}`,
      clear: (p) => ({
        ...p,
        organizationDomains: p.organizationDomains.filter((x) => x !== d),
      }),
    });
  }
  if (value.revenueMin != null || value.revenueMax != null) {
    chips.push({
      id: "rev",
      label: `Revenue: ${value.revenueMin ?? "…"}–${value.revenueMax ?? "…"}`,
      clear: (p) => ({
        ...p,
        revenueMin: undefined,
        revenueMax: undefined,
      }),
    });
  }
  if (value.currentlyUsingAnyOfTechnologyUids.length) {
    chips.push({
      id: "tech",
      label: `Tech: ${value.currentlyUsingAnyOfTechnologyUids.join(", ")}`,
      clear: (p) => ({ ...p, currentlyUsingAnyOfTechnologyUids: [] }),
    });
  }
  for (const t of value.qOrganizationJobTitles) {
    chips.push({
      id: `job-${t}`,
      label: `Hiring: ${t}`,
      clear: (p) => ({
        ...p,
        qOrganizationJobTitles: p.qOrganizationJobTitles.filter((x) => x !== t),
      }),
    });
  }
  if (
    value.latestFundingAmountMin != null ||
    value.latestFundingAmountMax != null ||
    value.totalFundingMin != null ||
    value.totalFundingMax != null
  ) {
    chips.push({
      id: "fund",
      label: "Funding filters",
      clear: (p) => ({
        ...p,
        latestFundingAmountMin: undefined,
        latestFundingAmountMax: undefined,
        totalFundingMin: undefined,
        totalFundingMax: undefined,
        latestFundingDateMin: "",
        latestFundingDateMax: "",
      }),
    });
  }
  for (const s of value.sources ?? []) {
    chips.push({
      id: `src-${s}`,
      label: `Source: ${s}`,
      clear: (p) => ({
        ...p,
        sources: (p.sources ?? []).filter((x) => x !== s),
      }),
    });
  }
  if (value.createdAfter || value.createdBefore) {
    chips.push({
      id: "created",
      label: `Created: ${value.createdAfter || "…"} → ${value.createdBefore || "…"}`,
      clear: (p) => ({ ...p, createdAfter: "", createdBefore: "" }),
    });
  }

  return chips;
}

export function CompanyFilterChips({
  value,
  onChange,
  onClear,
  personaLabel,
}: {
  value: CompanyFilterState;
  onChange: (next: CompanyFilterState) => void;
  /** Clear all filters and re-search (includes page search bar). */
  onClear?: () => void;
  personaLabel?: string;
}) {
  const chips = buildCompanyFilterChips(value).map((chip) =>
    chip.id === "persona" && personaLabel
      ? { ...chip, label: `Persona: ${personaLabel}` }
      : chip,
  );
  if (chips.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {chips.map((chip) => (
        <button
          key={chip.id}
          type="button"
          onClick={() => onChange(chip.clear(value))}
          className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/20 px-2 py-1 text-[11px] text-foreground hover:bg-muted/40"
        >
          <span className="max-w-[220px] truncate">{chip.label}</span>
          <X className="size-3 text-muted-foreground" />
        </button>
      ))}
      <button
        type="button"
        onClick={() =>
          onClear ? onClear() : onChange(EMPTY_COMPANY_FILTERS)
        }
        className="text-[11px] text-muted-foreground hover:text-foreground"
      >
        Clear all
      </button>
    </div>
  );
}
